import React, { useEffect, useRef } from "react";
import styles from "./style.module.scss";
import { Link } from "react-router-dom";
import { brandColours } from "../../../assets/brandColours";
import { useMsal } from "@azure/msal-react";
import { RouteConfig, routeConfigs } from "../../../routes/routeConfigs";
import { FiLogOut } from "react-icons/fi";
import { store } from "../../../state";
import runTimeVars from "../../../runTimeVars";

const enableMsalAuth = runTimeVars.enableMsalAuth !== "false";

function useOutsideAlerter(
  ref: React.RefObject<HTMLDivElement>,
  showNav: boolean,
  toggleNavNav: (show: boolean) => void,
) {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        ref.current &&
        !ref.current.contains(event.target as Node) &&
        showNav
      ) {
        toggleNavNav(false);
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, showNav, toggleNavNav]);
}

interface SideNavProps {
  showNav: boolean;
  toggleNav: (show: boolean) => void;
}

function SideNav(props: SideNavProps) {
  const { showNav, toggleNav } = props;

  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef, showNav, toggleNav);

  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  const permissions = store.getState().permissions.permissions;

  const showRouteInNavbar = (route: RouteConfig) => {
    if (!route.enabled || !route.showInNavBar) {
      return false;
    }

    if (!route.requiresPermission) {
      return true;
    }

    if (!route.permissionGroup || route.permissionBit === null) {
      return false;
    }

    const hasPermission =
      ((permissions?.[route.permissionGroup] ?? 0) >> route.permissionBit) & 1;

    return hasPermission;
  };

  const pages = Object.values(routeConfigs)
    .filter((route) => {
      return showRouteInNavbar(route);
    })
    .map((route) => ({ name: route.name, link: route.path, icon: route.icon }));

  const handleLogout = () => {
    instance
      .logoutRedirect({ account: account })
      .catch((error) => console.log(error));
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith("msal."))
      .forEach((key) => window.localStorage.removeItem(key));
    window.location.replace("/login");
  };

  return (
    <div
      ref={wrapperRef}
      className={styles.sideNav}
      style={{
        width: showNav ? "375px" : "0px",
        border: showNav ? "solid 3px black" : "none",
        backgroundColor: brandColours.gold,
      }}
    >
      {showNav ? (
        <>
          <div className={styles.pagesContainer}>
            {pages.map((item) => {
              return (
                <div key={item.name}>
                  {item.icon && <item.icon />}
                  <Link
                    to={item.link}
                    style={{ color: "inherit", textDecoration: "none" }}
                  >
                    {item.name}
                  </Link>
                </div>
              );
            })}
          </div>
          {(enableMsalAuth || account) && (
            <div
              className={styles.logout}
              key="logout"
              onClick={handleLogout}
              style={{ textDecoration: "none" }}
            >
              <FiLogOut className={styles.icon} />
              Logout
            </div>
          )}
        </>
      ) : (
        <></>
      )}
    </div>
  );
}

export default SideNav;
